import { calComLinkFI, calComLinkSE } from "../constants";
import { CountryCode, type SupportedLanguage } from "../types/common";

// Anchored at the scheme so the host is matched, not found. A wrong partial
// match silently books a DIFFERENT advisor: a backslash is a path separator to
// the URL parser but not to a regex, percent-encoding must not match its
// leading run, and a TEAM link's three segments must not collapse to the
// team's profile page.
const SEGMENT = String.raw`[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*`;

const CAL_COM_BOOKING_URL = new RegExp(
  String.raw`^https://cal\.com/(?:(team)/)?(?!team(?:/|$))(${SEGMENT})/(${SEGMENT})(?:\/(?=[?#]|$))?(?=[?#]|$)`,
);

const EMBED_LANGUAGES: readonly SupportedLanguage[] = ["sv", "en", "fi"];

const resolveEmbedLanguage = (
  locale?: string,
): SupportedLanguage | undefined => {
  const baseSubtag = locale?.split("-")[0]?.toLowerCase();

  return EMBED_LANGUAGES.find((language) => language === baseSubtag);
};

export const extractCalComBookingSlug = (
  link?: string | null,
): string | null => {
  const match = link?.trim().match(CAL_COM_BOOKING_URL);

  if (!match) return null;

  const teamPrefix = match[1] ? `${match[1]}/` : "";

  return `${teamPrefix}${match[2]}/${match[3]}`;
};

export const isCalComBookingUrl = (link?: string | null): boolean =>
  extractCalComBookingSlug(link) !== null;

const DEFAULT_LINK_BY_COUNTRY: Readonly<Record<CountryCode, string>> = {
  [CountryCode.SE]: calComLinkSE,
  [CountryCode.FI]: calComLinkFI,
};

export interface IResolveCalComLinkParams {
  countryCode: CountryCode;
  calendarLink?: string | null;
  locale?: string;
}

export interface IResolvedCalComLink {
  calLink: string | null;
  locale?: SupportedLanguage;
}

export const resolveCalComLink = ({
  countryCode,
  calendarLink,
  locale,
}: IResolveCalComLinkParams): IResolvedCalComLink => ({
  calLink:
    extractCalComBookingSlug(calendarLink) ??
    extractCalComBookingSlug(DEFAULT_LINK_BY_COUNTRY[countryCode]),
  locale: resolveEmbedLanguage(locale),
});
